import React, { useEffect, useState } from "react";
import { Modal, Select, Spin, message } from "antd";
import { userService } from "@iso/services";
import { isSuccess } from "@iso/lib/helpers/apiResponse";

const { Option } = Select;

/**
 * Modal for assigning a property user to one or more SDS files. SDS is
 * always property-scoped (one is_system folder per property), so there is
 * no corporate-group branch like in the file-manager modal.
 *
 * For single-file mode pass `initialUserId` so the picker is preselected.
 *
 * Kept as a separate component from the file-manager copy so SDS can
 * iterate on copy / validation rules independently.
 */
const SDSAssignUserModal = ({
  visible,
  propertyId,
  initialUserId = null,
  onCancel,
  onConfirm,
  confirmLoading = false,
  fileCount = 1,
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(initialUserId || undefined);

  useEffect(() => {
    if (!visible) return;
    setSelectedId(initialUserId || undefined);

    if (!propertyId) {
      setUsers([]);
      return;
    }
    setLoading(true);
    userService
      .getUserForProperty(propertyId)
      .then((res) => {
        // Verify success before reading the array so a failure response
        // does not silently render an empty list as if there were no users.
        if (isSuccess(res) && Array.isArray(res.users)) {
          setUsers(res.users);
        } else {
          setUsers([]);
        }
      })
      .catch((err) => {
        console.error("getUserForProperty failed", err);
        message.error("Failed to load property users");
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, [visible, propertyId, initialUserId]);

  const handleOk = () => {
    if (!selectedId) {
      // Unassign isn't supported yet (matches the file-manager guard —
      // see AttachmentController actionUpdate / actionBulkUpdate TODO).
      return;
    }
    onConfirm(selectedId);
  };

  const formatLabel = (user) => {
    const name = `${user.name || ""} ${user.surname || ""}`.trim();
    return name || user.email || `User #${user.id}`;
  };

  return (
    <Modal
      title={
        fileCount > 1
          ? `Assign user to ${fileCount} files`
          : "Assign user to file"
      }
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Assign"
      okButtonProps={{ disabled: !selectedId }}
      confirmLoading={confirmLoading}
      destroyOnClose
      zIndex={1200}
      wrapClassName="sds-bulk-modal"
    >
      <Spin spinning={loading}>
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder="Select a property user"
          value={selectedId}
          onChange={(value) => setSelectedId(value)}
          dropdownStyle={{ zIndex: 1300 }}
          filterOption={(input, option) =>
            option?.children?.toString().toLowerCase().includes(input.toLowerCase())
          }
        >
          {users.map((user) => (
            <Option key={user.id} value={user.id}>
              {formatLabel(user)}
            </Option>
          ))}
        </Select>
      </Spin>
      <div style={{ marginTop: 12, fontSize: 12, color: "#888" }}>
        Pick a property user — they will be assigned to{" "}
        {fileCount > 1 ? `all ${fileCount} selected files` : "this file"}.
      </div>
    </Modal>
  );
};

export default SDSAssignUserModal;
